import { Fragment } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { ChevronDown } from 'lucide-react'
import type { Tenant, StorefrontSettings, NavLink } from '@sifly/shared-types'
import { formatPhone, type LogoFamily } from '@sifly/shared-utils'
import { LogoContrastPlate } from './LogoContrastPlate'
import { hasNavHref } from './navHref'

// Local render-side mirror of the agent-foundation chrome registry. The
// canonical source is `packages/agent-foundation/src/registry/chrome.ts`;
// kept narrow here so shared-ui doesn't take a dependency on the runtime
// agent package. A vitest check in agent-foundation's chrome.test.ts
// keeps these in sync.
export type HeaderVariant =
  | 'classic_left'
  | 'centered'
  | 'overlay_hero'
  | 'split_logo_center'
  | 'dense_utility'
  | 'mega_dropdown'
  | 'minimal_ad_landing'
  | 'brand_showcase'

type ChromeAppearance = {
  /** Background fill — overrides `--color-bg-primary` when present. */
  background_hex?: string
  /** Foreground/link text color — overrides `--color-text-primary` when present. */
  text_hex?: string
  /** CTA + emphasis accent — overrides `--color-brand-primary` when present. */
  accent_hex?: string
  /** Optional border color — overrides `--color-border` when present. */
  border_hex?: string
  // Brand-aware surface (resolved at the layout boundary into the *_hex fields
  // above before render — the component itself only consumes the hexes).
  surface_source?: 'theme' | 'primary' | 'secondary' | 'custom'
  surface_shade?: number
  surface_custom_hex?: string
}

export type ChromeHeaderData = {
  utility_bar?: { enabled: boolean }
  cta?: { enabled: boolean; label: string; href: string; style: 'primary' | 'secondary' | 'ghost' }
  sticky_behavior?: 'sticky' | 'static' | 'sticky_on_scroll'
  logo_size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Per-surface logo override — a mark FAMILY; the storefront resolver picks
   *  light/dark within it by surface darkness. Resolved at the layout boundary,
   *  not here. */
  logo?: { family: LogoFamily }
  appearance?: ChromeAppearance
  // Chrome v2 slots (Phase A)
  search_affordance?: { enabled: boolean; placeholder?: string }
  language_switcher?: { enabled: boolean; languages?: string[] }
  inline_announcement?: { enabled: boolean; message: string; href?: string }
  // brand_showcase slots
  logo_badge?: { enabled: boolean }
  nav_case?: 'default' | 'uppercase'
}

export type ChromeHeaderOverride = { variant: HeaderVariant; data: ChromeHeaderData }

export type SiteHeaderProps = {
  tenant: Tenant
  settings: StorefrontSettings | null
  cartSlot?: React.ReactNode
  /** Search icon rendered before `accountSlot`. Storefronts pass a
   * `<SearchButton />` that opens a search overlay routing to `/search?q=`. */
  searchSlot?: React.ReactNode
  /** Account entry point rendered immediately before `cartSlot` in every
   * header variant. Storefronts pass a `<AccountButton />` that links to
   * `/account` — covers customers who need to reach the account surface
   * without a magic-link email. Optional so non-storefront consumers
   * (admin previews, etc.) don't have to render anything. */
  accountSlot?: React.ReactNode
  mobileNavSlot?: React.ReactNode
  /** Pre-resolved logo URL. The layout resolves the right mark per surface
   * via `resolveSurfaceLogo` (family override + computed darkness) and passes
   * the URL string here — this component stays presentational. */
  logoUrl?: string | null
  /** True when the resolved mark is shown on a mismatched-darkness surface and
   * needs the CSS contrast-plate to stay readable (a dealer who uploaded only
   * one tone of a family). Computed by the layout's resolver; additive +
   * optional so the existing string-only call sites keep working. */
  logoPlate?: boolean
  /** `media_items.content_bounds` for the resolved mark — where the artwork
   * sits inside its file, as fractions of the file box (migration 337).
   * Surfaces that composite the mark into a platform shape (the
   * brand_showcase badge) use it to size the MARK rather than the file, so
   * `logo_size` means the same apparent size for every dealer. Raw `jsonb`;
   * absent/invalid falls back to whole-file framing. */
  markBounds?: unknown
  /** SiteSmith Chrome mode override. When absent, renders the classic_left
   * default. Slots fall through to render defaults independently. */
  override?: ChromeHeaderOverride | null
  /** True on the storefront's home (`is_index`) page. `overlay_hero` uses
   * this to render transparent over the hero on home and solid elsewhere. */
  isHomePage?: boolean
  /** Derived defaults computed by the storefront layout from the actual
   * published state of singleton pages (about / faq / contact). When
   * provided, wins over the static tier fallback below. Authored
   * `settings.menu_links` always wins over both. */
  defaultMenuEntries?: NavLink[]
  /** Optional analytics hook. Fired on every primary nav-link click (desktop
   * nav items, mega-menu columns/leaves, utility-bar items). shared-ui stays
   * analytics-agnostic — the storefront passes a callback that routes into its
   * consent-gated `track('nav_click', meta)` path. Must be supplied from a
   * client boundary; never preventDefault. */
  onLinkClick?: (meta: { label: string; href: string }) => void
}

// `xl` targets badge-style marks (circular logos with interior text) that need
// ~80px to be legible; it downshifts to 56px on mobile so the 390px header
// keeps its proportions.
const LOGO_HEIGHT_BY_SIZE = { sm: 'h-6', md: 'h-8', lg: 'h-12', xl: 'h-14 md:h-20' } as const

// An xl logo exceeds every variant's fixed row height, so the row swaps to
// min-height + vertical padding and grows with the logo instead of clipping
// it. Literal class strings — Tailwind's scanner can't see interpolated names.
const XL_ROW_BY_BASE = {
  'h-14': 'min-h-14 py-2',
  'h-16': 'min-h-16 py-2',
  'h-20': 'min-h-20 py-2',
} as const

function rowHeightCls(
  base: keyof typeof XL_ROW_BY_BASE,
  logoSize: NonNullable<ChromeHeaderData['logo_size']>,
): string {
  return logoSize === 'xl' ? XL_ROW_BY_BASE[base] : base
}

// Fallback nav entries when a tenant has not configured `menu_links` yet.
// Tier-aware as of 2026-05-24 (migration 201): shop tier was designed
// as an add-on to a dealer's existing main website, so its default nav
// is just "Shop" — About / FAQ / Contact default-draft on shop tier
// (the dealer's main site already carries those pages, or the dealer
// can publish + set `content_pages.external_url` to bounce visitors
// back). Signature tiers get the full surface because the SiFly site
// IS their site.
//
// Dealers customize via `menu_links` regardless of tier; this is only
// the fallback.
const SHOP_DEFAULT_MENU_ENTRIES: NavLink[] = [
  { id: 'shop', label: 'Shop', href: '/products' },
]

const SIGNATURE_DEFAULT_MENU_ENTRIES: NavLink[] = [
  { id: 'shop', label: 'Shop', href: '/products' },
  { id: 'about', label: 'About', href: '/about' },
  { id: 'faq', label: 'FAQ', href: '/faq' },
  { id: 'contact', label: 'Contact', href: '/contact' },
]

function defaultEntriesForTier(tier: Tenant['site_tier']): NavLink[] {
  return tier === 'shop' ? SHOP_DEFAULT_MENU_ENTRIES : SIGNATURE_DEFAULT_MENU_ENTRIES
}

// Exported for BrandShowcaseHeader (render-time import; same file-pair
// pattern as SiteFooter ↔ BrandShowcaseFooter).
export function effectiveMenuEntries(
  configured: NavLink[],
  tier: Tenant['site_tier'],
  derived?: NavLink[],
): NavLink[] {
  if (configured.length > 0) return configured
  if (derived && derived.length > 0) return derived
  return defaultEntriesForTier(tier)
}

/**
 * Convert a chrome `appearance` block into an inline-style object that
 * overrides theme-token CSS custom properties scoped to this element + its
 * children. Tailwind classes like `bg-[var(--color-bg-primary)]` resolve against
 * the nearest defined custom property, so writing the var inline at the
 * header/footer root is the surgical way to repaint chrome without
 * disturbing the rest of the page.
 */
export function appearanceStyle(appearance: ChromeAppearance | undefined): React.CSSProperties | undefined {
  if (!appearance) return undefined
  const s: Record<string, string> = {}
  if (appearance.background_hex) s['--color-bg-primary'] = appearance.background_hex
  if (appearance.background_hex) s['--color-bg-secondary'] = appearance.background_hex
  if (appearance.text_hex) {
    s['--color-text-primary'] = appearance.text_hex
    s['--color-text-secondary'] = appearance.text_hex
    s['--color-text-muted'] = appearance.text_hex
  }
  if (appearance.accent_hex) s['--color-brand-primary'] = appearance.accent_hex
  if (appearance.border_hex) s['--color-border'] = appearance.border_hex
  return Object.keys(s).length > 0 ? (s as React.CSSProperties) : undefined
}

function stickyClasses(behavior: ChromeHeaderData['sticky_behavior']): string {
  if (behavior === 'static') return ''
  // `sticky_on_scroll` falls back to sticky at render time; intersection-
  // observer reveal-on-scroll is reserved for a future pass.
  return 'sticky top-0 z-30'
}

function ctaClasses(style: NonNullable<ChromeHeaderData['cta']>['style']): string {
  switch (style) {
    case 'primary':
      // `--color-brand-primary` is only defined when a header `accent_hex`
      // appearance override is set (see styleVarsFor). Fall back to the global
      // `--color-cta` token (always emitted by the tenant theme) so the button
      // is visible by default — otherwise it renders white-on-white. cta-text
      // (#fff fallback) matches the white assumption the accent path also uses.
      return 'inline-flex items-center whitespace-nowrap px-4 h-9 rounded-md bg-[var(--color-brand-primary,var(--color-cta))] text-[var(--color-cta-text,#fff)] text-sm font-medium hover:opacity-90 transition-opacity'
    case 'secondary':
      return 'inline-flex items-center whitespace-nowrap px-4 h-9 rounded-md border border-[var(--color-border)] text-[var(--color-text-primary)] text-sm font-medium hover:bg-[var(--color-bg-secondary)] transition-colors'
    case 'ghost':
      return 'inline-flex items-center whitespace-nowrap px-3 h-9 text-[var(--color-text-primary)] text-sm font-medium hover:opacity-80 transition-opacity'
  }
}

function HeaderCTA({ cta }: { cta: NonNullable<ChromeHeaderData['cta']> }) {
  if (!cta.enabled) return null
  return (
    <Link href={cta.href} className={ctaClasses(cta.style)} data-chrome-target="header:cta">
      {cta.label}
    </Link>
  )
}

/**
 * Trailing action cluster shared by every header variant: optional CTA plus
 * the search / account / cart / mobile-nav slots.
 *
 * The slots are React elements created in the storefront's RootLayout (a
 * server component) and threaded down as props. Each variant renders them
 * inside a flex row, where React validates them as a keyed list — so without
 * keys every page logs a "unique key prop" warning attributed to the variant.
 * Keys set on the slot elements at creation in RootLayout don't survive the
 * RSC server→client boundary, so they're applied here, on the client, in one
 * place — keeping all six variants consistent and warning-free.
 */
export function HeaderActions({
  cta,
  searchSlot,
  accountSlot,
  cartSlot,
  mobileNavSlot,
  ctaLast = false,
}: Pick<SiteHeaderProps, 'searchSlot' | 'accountSlot' | 'cartSlot' | 'mobileNavSlot'> & {
  cta?: ChromeHeaderData['cta']
  /** Render the CTA AFTER the icon cluster (far-right anchor) instead of
   *  before it. Opt-in per variant — the historical order (CTA first) is the
   *  default so no existing variant shifts. */
  ctaLast?: boolean
}) {
  const ctaEl = cta ? <HeaderCTA key="cta" cta={cta} /> : null
  return (
    <>
      {!ctaLast && ctaEl}
      <Fragment key="search">{searchSlot}</Fragment>
      <Fragment key="account">{accountSlot}</Fragment>
      <Fragment key="cart">{cartSlot}</Fragment>
      {ctaLast && ctaEl}
      <Fragment key="mobile-nav">{mobileNavSlot}</Fragment>
    </>
  )
}

function UtilityBar({ entries, onLinkClick }: { entries: NavLink[]; onLinkClick?: SiteHeaderProps['onLinkClick'] }) {
  if (entries.length === 0) return null
  return (
    <div
      className="border-b border-[var(--color-border)]/60 bg-[var(--color-bg-secondary)]"
      data-chrome-target="header:utility_bar"
    >
      <div className="mx-auto max-w-[var(--max-width-page)] px-4 h-8 flex items-center justify-end gap-4">
        {entries.map((entry) =>
          hasNavHref(entry) ? (
            <Link
              key={entry.id}
              href={entry.href}
              className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
              data-chrome-target={`header:utility_item:${entry.id}`}
              onClick={onLinkClick ? () => onLinkClick({ label: entry.label, href: entry.href }) : undefined}
            >
              {entry.label}
            </Link>
          ) : (
            // The utility row has no dropdowns, so a link-less entry is plain
            // text (hours, a tagline) rather than `<a href="">`.
            <span
              key={entry.id}
              className="text-xs text-[var(--color-text-muted)]"
              data-chrome-target={`header:utility_item:${entry.id}`}
            >
              {entry.label}
            </span>
          ),
        )}
      </div>
    </div>
  )
}

export function Logo({
  tenant,
  logoUrl,
  size,
  plate,
}: {
  tenant: Tenant
  logoUrl?: string | null
  size: NonNullable<ChromeHeaderData['logo_size']>
  plate?: boolean
}) {
  const heightCls = LOGO_HEIGHT_BY_SIZE[size]
  return (
    <Link href="/" className="shrink-0" data-chrome-target="header:logo">
      {logoUrl ? (
        <LogoContrastPlate plate={plate}>
          <Image
            src={logoUrl}
            alt={tenant.name}
            width={120}
            height={32}
            className={`${heightCls} w-auto object-contain`}
          />
        </LogoContrastPlate>
      ) : (
        <span
          className="font-semibold text-lg text-[var(--color-text-primary)]"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {tenant.name}
        </span>
      )}
    </Link>
  )
}

const NAV_ITEM_CLASS =
  'inline-flex items-center gap-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors'

function NavChevron() {
  return (
    <ChevronDown
      size={14}
      className="opacity-70 transition-transform group-hover:rotate-180"
      aria-hidden
    />
  )
}

/**
 * Desktop nav — data-driven dropdown / mega-menu rendering. Identical
 * across all variants; only the wrapping layout changes per variant.
 */
export function DesktopNav({ menuEntries, onLinkClick }: { menuEntries: NavLink[]; onLinkClick?: SiteHeaderProps['onLinkClick'] }) {
  if (menuEntries.length === 0) return null
  return (
    <nav className="hidden md:flex items-center gap-6">
      {menuEntries.map((entry) => {
        const children = entry.children ?? []
        const hasChildren = children.length > 0
        const hasMegaMenu =
          hasChildren && children.some((c) => (c.children?.length ?? 0) > 0)
        return (
          <div key={entry.id} className="relative group py-3 -my-3">
            {hasNavHref(entry) ? (
              <Link
                href={entry.href}
                className={NAV_ITEM_CLASS}
                style={{ fontFamily: 'var(--font-body)' }}
                data-chrome-target={`header:nav_item:${entry.id}`}
                onClick={onLinkClick ? () => onLinkClick({ label: entry.label, href: entry.href }) : undefined}
              >
                {entry.label}
                {hasChildren && <NavChevron />}
              </Link>
            ) : (
              // A parent with no link of its own is a toggle, not `<a href="">`
              // (which links to the current page). Focus opens its dropdown via
              // `group-focus-within` below, so it stays keyboard-reachable.
              <button
                type="button"
                className={`${NAV_ITEM_CLASS} cursor-default`}
                style={{ fontFamily: 'var(--font-body)' }}
                data-chrome-target={`header:nav_item:${entry.id}`}
                aria-haspopup={hasChildren ? 'true' : undefined}
              >
                {entry.label}
                {hasChildren && <NavChevron />}
              </button>
            )}
            {hasMegaMenu ? (
              <div className="absolute left-0 top-full hidden group-hover:block group-focus-within:block z-50">
                <div
                  className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] shadow-md p-5 grid gap-6"
                  style={{
                    gridTemplateColumns: `repeat(${Math.min(children.length, 4)}, minmax(180px, 1fr))`,
                  }}
                >
                  {children.map((column) => {
                    const columnChildren = column.children ?? []
                    return (
                      <div key={column.id}>
                        {hasNavHref(column) ? (
                          <Link
                            href={column.href}
                            className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2 hover:opacity-80 transition-opacity"
                            style={{ fontFamily: 'var(--font-heading)' }}
                            onClick={onLinkClick ? () => onLinkClick({ label: column.label, href: column.href }) : undefined}
                          >
                            {column.label}
                          </Link>
                        ) : (
                          <p
                            className="text-sm font-semibold text-[var(--color-text-primary)] mb-2"
                            style={{ fontFamily: 'var(--font-heading)' }}
                          >
                            {column.label}
                          </p>
                        )}
                        {columnChildren.length > 0 && (
                          <ul className="space-y-1.5">
                            {columnChildren.map((leaf) => (
                              <li key={leaf.id}>
                                {hasNavHref(leaf) ? (
                                  <Link
                                    href={leaf.href}
                                    className="block text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
                                    onClick={onLinkClick ? () => onLinkClick({ label: leaf.label, href: leaf.href }) : undefined}
                                  >
                                    {leaf.label}
                                  </Link>
                                ) : (
                                  <span className="block text-sm text-[var(--color-text-secondary)]">{leaf.label}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : hasChildren ? (
              <div className="absolute left-0 top-full hidden group-hover:block group-focus-within:block z-50">
                <ul className="mt-2 min-w-44 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] shadow-md py-2">
                  {children.map((child) => (
                    <li key={child.id}>
                      {hasNavHref(child) ? (
                        <Link
                          href={child.href}
                          className="block px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
                          onClick={onLinkClick ? () => onLinkClick({ label: child.label, href: child.href }) : undefined}
                        >
                          {child.label}
                        </Link>
                      ) : (
                        // A child left without a link in the portal editor is
                        // text, never `<a href="">`.
                        <span className="block px-4 py-2 text-sm text-[var(--color-text-secondary)]">{child.label}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )
      })}
    </nav>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Variant: classic_left — preserves the prior single-layout behavior.
// ─────────────────────────────────────────────────────────────────────

function ClassicLeftHeader(props: SiteHeaderProps) {
  const { tenant, settings, cartSlot, accountSlot, searchSlot, mobileNavSlot, logoUrl } = props
  const data = props.override?.data ?? {}
  const headerEntries: NavLink[] = settings?.header_nav?.entries ?? []
  const menuEntries: NavLink[] = effectiveMenuEntries(
    settings?.menu_links?.entries ?? [],
    tenant.site_tier,
    props.defaultMenuEntries,
  )
  const showUtility = data.utility_bar?.enabled !== false
  const logoSize = data.logo_size ?? 'md'

  return (
    <header
      className={`${stickyClasses(data.sticky_behavior ?? 'sticky')} border-b bg-[var(--color-bg-primary)] border-[var(--color-border)] backdrop-blur-sm text-[var(--color-text-primary)]`}
      style={appearanceStyle(data.appearance)}
      data-component="SiteHeader"
      data-variant="classic_left"
      data-section-ref="chrome:header:classic_left"
    >
      {showUtility && <UtilityBar entries={headerEntries} onLinkClick={props.onLinkClick} />}
      <div className={`mx-auto max-w-[var(--max-width-page)] px-4 ${rowHeightCls('h-16', logoSize)} flex items-center justify-between`}>
        <Logo tenant={tenant} logoUrl={logoUrl} size={logoSize} plate={props.logoPlate} />
        <DesktopNav menuEntries={menuEntries} onLinkClick={props.onLinkClick} />
        <div className="flex items-center gap-4">
          <HeaderActions
            cta={data.cta}
            searchSlot={searchSlot}
            accountSlot={accountSlot}
            cartSlot={cartSlot}
            mobileNavSlot={mobileNavSlot}
          />
        </div>
      </div>
    </header>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Variant: centered — logo centered between split nav columns, no utility
// bar (deliberately — centered logo is a strong visual; doubling with a
// thin bar above muddles the hierarchy).
// ─────────────────────────────────────────────────────────────────────

function CenteredHeader(props: SiteHeaderProps) {
  const { tenant, settings, cartSlot, accountSlot, searchSlot, mobileNavSlot, logoUrl } = props
  const data = props.override?.data ?? {}
  const menuEntries: NavLink[] = effectiveMenuEntries(
    settings?.menu_links?.entries ?? [],
    tenant.site_tier,
    props.defaultMenuEntries,
  )
  const logoSize = data.logo_size ?? 'md'
  // Split menu entries in half — left + right of the centered logo on desktop.
  const half = Math.ceil(menuEntries.length / 2)
  const leftEntries = menuEntries.slice(0, half)
  const rightEntries = menuEntries.slice(half)

  return (
    <header
      className={`${stickyClasses(data.sticky_behavior ?? 'sticky')} border-b bg-[var(--color-bg-primary)] border-[var(--color-border)] text-[var(--color-text-primary)]`}
      style={appearanceStyle(data.appearance)}
      data-component="SiteHeader"
      data-variant="centered"
      data-section-ref="chrome:header:centered"
    >
      <div className={`mx-auto max-w-[var(--max-width-page)] px-4 ${rowHeightCls('h-20', logoSize)} grid grid-cols-[1fr_auto_1fr] items-center gap-4`}>
        <div className="hidden md:flex items-center justify-start">
          <DesktopNav menuEntries={leftEntries} onLinkClick={props.onLinkClick} />
        </div>
        <Logo tenant={tenant} logoUrl={logoUrl} size={logoSize} plate={props.logoPlate} />
        <div className="flex items-center justify-end gap-4">
          <div className="hidden md:flex">
            <DesktopNav menuEntries={rightEntries} onLinkClick={props.onLinkClick} />
          </div>
          <HeaderActions
            cta={data.cta}
            searchSlot={searchSlot}
            accountSlot={accountSlot}
            cartSlot={cartSlot}
            mobileNavSlot={mobileNavSlot}
          />
        </div>
      </div>
    </header>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Variant: overlay_hero — transparent over the hero on home, solid
// elsewhere. Uses absolute positioning on home (so hero can paint
// edge-to-edge underneath); falls back to the classic sticky layout
// off-home. No utility bar variant; CTA always rendered when configured.
// ─────────────────────────────────────────────────────────────────────

function OverlayHeroHeader(props: SiteHeaderProps) {
  const { tenant, settings, cartSlot, accountSlot, searchSlot, mobileNavSlot, logoUrl, isHomePage } = props
  const data = props.override?.data ?? {}
  const menuEntries: NavLink[] = effectiveMenuEntries(
    settings?.menu_links?.entries ?? [],
    tenant.site_tier,
    props.defaultMenuEntries,
  )
  const logoSize = data.logo_size ?? 'md'
  // On home: float on top of the hero, no border, white-ish text via
  // an extra class hook (storefront can scope-style via [data-overlay]).
  // Off-home: regular sticky header for navigation continuity.
  const onHomeClasses =
    'absolute inset-x-0 top-0 z-30 bg-transparent text-white [&_a]:text-white/90 [&_a:hover]:text-white'
  const offHomeClasses = `${stickyClasses(data.sticky_behavior ?? 'sticky')} border-b bg-[var(--color-bg-primary)] border-[var(--color-border)]`

  return (
    <header
      className={isHomePage ? onHomeClasses : offHomeClasses}
      // overlay_hero is intentionally transparent over the hero on home;
      // appearance only paints in the off-home (solid) layout.
      style={isHomePage ? undefined : appearanceStyle(data.appearance)}
      data-component="SiteHeader"
      data-variant="overlay_hero"
      data-overlay={isHomePage ? '1' : '0'}
      data-section-ref="chrome:header:overlay_hero"
    >
      <div className={`mx-auto max-w-[var(--max-width-page)] px-4 ${rowHeightCls('h-16', logoSize)} flex items-center justify-between`}>
        <Logo tenant={tenant} logoUrl={logoUrl} size={logoSize} plate={props.logoPlate} />
        <DesktopNav menuEntries={menuEntries} onLinkClick={props.onLinkClick} />
        <div className="flex items-center gap-4">
          <HeaderActions
            cta={data.cta}
            searchSlot={searchSlot}
            accountSlot={accountSlot}
            cartSlot={cartSlot}
            mobileNavSlot={mobileNavSlot}
          />
        </div>
      </div>
    </header>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Variant: split_logo_center — logo center, nav split L+R. Similar
// silhouette to `centered` but explicitly two equal-weight nav columns.
// ─────────────────────────────────────────────────────────────────────

function SplitLogoCenterHeader(props: SiteHeaderProps) {
  const { tenant, settings, cartSlot, accountSlot, searchSlot, mobileNavSlot, logoUrl } = props
  const data = props.override?.data ?? {}
  const menuEntries: NavLink[] = effectiveMenuEntries(
    settings?.menu_links?.entries ?? [],
    tenant.site_tier,
    props.defaultMenuEntries,
  )
  const logoSize = data.logo_size ?? 'md'
  const half = Math.ceil(menuEntries.length / 2)
  const leftEntries = menuEntries.slice(0, half)
  const rightEntries = menuEntries.slice(half)

  return (
    <header
      className={`${stickyClasses(data.sticky_behavior ?? 'sticky')} border-b bg-[var(--color-bg-primary)] border-[var(--color-border)] text-[var(--color-text-primary)]`}
      style={appearanceStyle(data.appearance)}
      data-component="SiteHeader"
      data-variant="split_logo_center"
      data-section-ref="chrome:header:split_logo_center"
    >
      <div className={`mx-auto max-w-[var(--max-width-page)] px-4 ${rowHeightCls('h-20', logoSize)} grid grid-cols-3 items-center gap-4`}>
        <div className="hidden md:flex justify-start">
          <DesktopNav menuEntries={leftEntries} onLinkClick={props.onLinkClick} />
        </div>
        <div className="flex justify-center">
          <Logo tenant={tenant} logoUrl={logoUrl} size={logoSize} plate={props.logoPlate} />
        </div>
        <div className="flex items-center justify-end gap-4">
          <div className="hidden md:flex">
            <DesktopNav menuEntries={rightEntries} onLinkClick={props.onLinkClick} />
          </div>
          <HeaderActions
            cta={data.cta}
            searchSlot={searchSlot}
            accountSlot={accountSlot}
            cartSlot={cartSlot}
            mobileNavSlot={mobileNavSlot}
          />
        </div>
      </div>
    </header>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Variant: dense_utility — compact header with prominent utility bar
// (phone / hours / inline CTA on the same row), main nav on the second.
// Optimized for service-business sites where the phone is the funnel.
// ─────────────────────────────────────────────────────────────────────

function DenseUtilityHeader(props: SiteHeaderProps) {
  const { tenant, settings, cartSlot, accountSlot, searchSlot, mobileNavSlot, logoUrl } = props
  const data = props.override?.data ?? {}
  const headerEntries: NavLink[] = settings?.header_nav?.entries ?? []
  const menuEntries: NavLink[] = effectiveMenuEntries(
    settings?.menu_links?.entries ?? [],
    tenant.site_tier,
    props.defaultMenuEntries,
  )
  const logoSize = data.logo_size ?? 'sm'
  const phone = tenant.show_public_phone !== false ? tenant.phone : null

  return (
    <header
      className={`${stickyClasses(data.sticky_behavior ?? 'sticky')} border-b bg-[var(--color-bg-primary)] border-[var(--color-border)] text-[var(--color-text-primary)]`}
      style={appearanceStyle(data.appearance)}
      data-component="SiteHeader"
      data-variant="dense_utility"
      data-section-ref="chrome:header:dense_utility"
    >
      <div className="border-b border-[var(--color-border)]/60 bg-[var(--color-bg-secondary)]">
        <div className="mx-auto max-w-[var(--max-width-page)] px-4 h-9 flex items-center justify-between text-xs gap-4">
          <div className="flex items-center gap-4 text-[var(--color-text-muted)]">
            {phone && (
              <a href={`tel:${phone}`} className="hover:text-[var(--color-text-primary)] transition-colors">
                {formatPhone(phone)}
              </a>
            )}
            {headerEntries.map((entry) =>
              hasNavHref(entry) ? (
                <Link
                  key={entry.id}
                  href={entry.href}
                  className="hover:text-[var(--color-text-primary)] transition-colors"
                  data-chrome-target={`header:utility_item:${entry.id}`}
                  onClick={props.onLinkClick ? () => props.onLinkClick!({ label: entry.label, href: entry.href }) : undefined}
                >
                  {entry.label}
                </Link>
              ) : (
                // The utility row has no dropdowns, so a link-less entry is
                // plain text (hours, a tagline) rather than `<a href="">`.
                <span key={entry.id} data-chrome-target={`header:utility_item:${entry.id}`}>
                  {entry.label}
                </span>
              ),
            )}
          </div>
          {data.cta?.enabled && (
            <Link
              href={data.cta.href}
              className="text-[var(--brand-primary)] font-medium hover:opacity-80 transition-opacity"
              data-chrome-target="header:cta"
            >
              {data.cta.label}
            </Link>
          )}
        </div>
      </div>
      <div className={`mx-auto max-w-[var(--max-width-page)] px-4 ${rowHeightCls('h-14', logoSize)} flex items-center justify-between`}>
        <Logo tenant={tenant} logoUrl={logoUrl} size={logoSize} plate={props.logoPlate} />
        <DesktopNav menuEntries={menuEntries} onLinkClick={props.onLinkClick} />
        <div className="flex items-center gap-3">
          <HeaderActions
            searchSlot={searchSlot}
            accountSlot={accountSlot}
            cartSlot={cartSlot}
            mobileNavSlot={mobileNavSlot}
          />
        </div>
      </div>
    </header>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Variant: mega_dropdown — same overall layout as classic_left, but the
// renderer asserts mega-menu rendering when entries have nested children.
// The existing `DesktopNav` already handles mega columns when children-of-
// children are present; this variant is the explicit selector so dealers
// can opt in without restructuring their menu_links data.
// ─────────────────────────────────────────────────────────────────────

function MegaDropdownHeader(props: SiteHeaderProps) {
  const { tenant, settings, cartSlot, accountSlot, searchSlot, mobileNavSlot, logoUrl } = props
  const data = props.override?.data ?? {}
  const headerEntries: NavLink[] = settings?.header_nav?.entries ?? []
  const menuEntries: NavLink[] = effectiveMenuEntries(
    settings?.menu_links?.entries ?? [],
    tenant.site_tier,
    props.defaultMenuEntries,
  )
  const showUtility = data.utility_bar?.enabled !== false
  const logoSize = data.logo_size ?? 'md'

  return (
    <header
      className={`${stickyClasses(data.sticky_behavior ?? 'sticky')} border-b bg-[var(--color-bg-primary)] border-[var(--color-border)] text-[var(--color-text-primary)]`}
      style={appearanceStyle(data.appearance)}
      data-component="SiteHeader"
      data-variant="mega_dropdown"
      data-section-ref="chrome:header:mega_dropdown"
    >
      {showUtility && <UtilityBar entries={headerEntries} onLinkClick={props.onLinkClick} />}
      <div className={`mx-auto max-w-[var(--max-width-page)] px-4 ${rowHeightCls('h-16', logoSize)} flex items-center justify-between`}>
        <Logo tenant={tenant} logoUrl={logoUrl} size={logoSize} plate={props.logoPlate} />
        <DesktopNav menuEntries={menuEntries} onLinkClick={props.onLinkClick} />
        <div className="flex items-center gap-4">
          <HeaderActions
            cta={data.cta}
            searchSlot={searchSlot}
            accountSlot={accountSlot}
            cartSlot={cartSlot}
            mobileNavSlot={mobileNavSlot}
          />
        </div>
      </div>
    </header>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Dispatcher
// ─────────────────────────────────────────────────────────────────────

import { MinimalAdLandingHeader } from './MinimalAdLandingHeader'
import { BrandShowcaseHeader } from './BrandShowcaseHeader'

export function SiteHeader(props: SiteHeaderProps) {
  const variant: HeaderVariant = props.override?.variant ?? 'classic_left'
  switch (variant) {
    case 'brand_showcase':
      return <BrandShowcaseHeader {...props} />
    case 'centered':
      return <CenteredHeader {...props} />
    case 'overlay_hero':
      return <OverlayHeroHeader {...props} />
    case 'split_logo_center':
      return <SplitLogoCenterHeader {...props} />
    case 'dense_utility':
      return <DenseUtilityHeader {...props} />
    case 'mega_dropdown':
      return <MegaDropdownHeader {...props} />
    case 'minimal_ad_landing':
      return (
        <MinimalAdLandingHeader
          tenant={props.tenant}
          logoUrl={props.logoUrl}
          logoPlate={props.logoPlate}
          data={props.override?.data ?? {}}
        />
      )
    case 'classic_left':
    default:
      return <ClassicLeftHeader {...props} />
  }
}
